/**
 * MentionSuggestionPopover — the autocomplete UI for `@mentions`.
 *
 * Wires into TipTap's `Mention` extension via the `suggestion.render`
 * lifecycle. The suggestion plugin invokes `onStart` / `onUpdate` /
 * `onKeyDown` / `onExit` as the user types after `@`, and we translate
 * those into React state on a single mounted popover instance.
 *
 * Why this instead of `tippy.js`?
 *
 *   `@tiptap/extension-bubble-menu` uses tippy internally, but tippy is
 *   not a hoisted direct dependency of `3mote-fe-new` — it's a
 *   transitive dep of bubble-menu, hidden behind pnpm's strict isolation.
 *   The Phase 3b scope explicitly forbids new editor deps. So we render
 *   the popover via `createPortal` to `document.body` and position it
 *   absolutely from the suggestion's `clientRect()` — same UX, zero new
 *   deps.
 *
 * Keyboard contract:
 *   - ArrowDown / ArrowUp — move highlight (with wraparound).
 *   - Enter — commit highlighted item.
 *   - Escape — close popover (suggestion plugin handles teardown).
 *
 * The component is mounted exactly once per active suggestion session.
 * `useImperativeHandle` exposes `onKeyDown` so the suggestion plugin can
 * forward editor key events to it without React refs leaking out.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { AgentModelLine, AiTag } from '@/components/AiTag'
import { Avatar } from '@/components/Avatar'

import type { MentionMember } from './use-project-members-for-mention'

export interface MentionSuggestionItem extends MentionMember {}

export interface MentionSuggestionPopoverProps {
  /** Called on click-outside the popover. Fires only when the click target sits outside both the popover and any editor contenteditable. */
  onDismiss?: () => void
  items: MentionSuggestionItem[]
  command: (item: MentionSuggestionItem) => void
  /**
   * Anchor rect from `@tiptap/suggestion` — the bounding box of the
   * trigger character `@` in viewport coordinates. We position the
   * popover just below it.
   */
  clientRect: (() => DOMRect | null) | null
}

export interface MentionSuggestionPopoverHandle {
  onKeyDown: (event: KeyboardEvent) => boolean
}

export const MentionSuggestionPopover = forwardRef<
  MentionSuggestionPopoverHandle,
  MentionSuggestionPopoverProps
>(function MentionSuggestionPopover({ items, command, clientRect, onDismiss }, ref) {
  const { t } = useTranslation('chat')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listRef = useRef<HTMLUListElement | null>(null)

  // Dismiss on click-outside-the-popover-AND-outside-the-editor. Clicks
  // inside an editor's contenteditable are intentionally NOT dismissals
  // — the suggestion plugin's own caret-move logic exits naturally when
  // the caret leaves the trigger range, so a re-position click inside
  // the same editor shouldn't kill the popover.
  useEffect(() => {
    if (!onDismiss) return
    const handler = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (listRef.current?.contains(target)) return
      if (target.closest('[contenteditable="true"]')) return
      onDismiss()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onDismiss])

  // Reset highlight whenever the filtered list changes. The `items`
  // prop is a fresh array per `onUpdate` from the suggestion plugin,
  // so its identity reliably changes when the query mutates.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency on items prop identity is intentional
  useEffect(() => {
    setSelectedIndex(0)
  }, [items])

  const commit = (index: number) => {
    const item = items[index]
    if (!item) return
    command(item)
  }

  useImperativeHandle(ref, () => ({
    onKeyDown(event) {
      if (event.key === 'ArrowDown') {
        if (items.length === 0) return false
        setSelectedIndex((prev) => (prev + 1) % items.length)
        return true
      }
      if (event.key === 'ArrowUp') {
        if (items.length === 0) return false
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length)
        return true
      }
      if (event.key === 'Enter') {
        if (items.length === 0) return false
        commit(selectedIndex)
        return true
      }
      // Let Escape (and everything else) pass through to the suggestion
      // plugin / editor.
      return false
    },
  }))

  // Compute popover position from the anchor rect. Recomputed on every
  // render — the suggestion plugin re-fires onUpdate on each keystroke
  // anyway, so this is cheap.
  const rect = clientRect ? clientRect() : null
  if (!rect) {
    // No anchor yet — don't render. The plugin will call us again with
    // a valid rect on next tick.
    return null
  }

  // Slack-style positioning. When the trigger lives inside an
  // element marked `[data-chat-composer-surface]` (the chat composer's
  // outer wrapper), dock the popover ABOVE that surface and stretch it
  // to match the composer's width. Otherwise fall back to the legacy
  // below-the-caret positioning used by card-description / comment
  // mentions.
  const triggerElement =
    typeof document !== 'undefined'
      ? document.elementFromPoint(rect.left, rect.top + Math.max(1, rect.height - 1))
      : null
  const composerSurface =
    triggerElement instanceof Element
      ? (triggerElement.closest('[data-chat-composer-surface]') as HTMLElement | null)
      : null
  const composerRect = composerSurface?.getBoundingClientRect() ?? null

  const SLACK_GAP_PX = 4
  // Flip-above detection for the standard (non-composer) case. When the
  // caret sits near the bottom of the viewport, the default below-the-
  // caret popover runs off-screen and the list is unreachable. We
  // measure the rendered popover height post-mount and re-anchor above
  // the trigger when there isn't enough room below.
  //
  // First render uses a conservative below-the-caret position; the
  // useLayoutEffect below measures the popover and flips to above if
  // needed. The composer-surface dock path is untouched (it always
  // docks above by design, no measurement needed).
  const [flipAbove, setFlipAbove] = useState(false)
  // `items` is the re-run trigger — not read in the body. When the
  // filtered list shrinks/grows the popover's rendered height changes,
  // which changes the flip decision. Biome flags this as unused; the
  // dependency is correct.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when item list height changes
  useLayoutEffect(() => {
    if (composerRect) {
      setFlipAbove(false)
      return
    }
    const el = listRef.current
    const r = clientRect ? clientRect() : null
    if (!el || !r) return
    const viewportH = window.innerHeight
    const popoverH = el.offsetHeight
    const spaceBelow = viewportH - r.bottom
    const spaceAbove = r.top
    // Flip when the popover (plus its gap) doesn't fit below AND there's
    // more room above than below. Sticky decision: once we've flipped,
    // small viewport shifts don't bounce the popover back-and-forth.
    const shouldFlip = spaceBelow < popoverH + SLACK_GAP_PX && spaceAbove > spaceBelow
    setFlipAbove(shouldFlip)
    // Recompute on every keystroke (items prop identity changes) and on
    // every layout shift the rect captures. clientRect identity is stable
    // for a session; items change on each filter.
  }, [items, clientRect, composerRect])

  const positionStyle: { top: number; left: number; width?: number } = composerRect
    ? {
        top: composerRect.top + window.scrollY - SLACK_GAP_PX,
        left: composerRect.left + window.scrollX,
        width: composerRect.width,
      }
    : flipAbove
      ? {
          top: rect.top + window.scrollY - SLACK_GAP_PX,
          left: rect.left + window.scrollX,
        }
      : {
          top: rect.bottom + window.scrollY + SLACK_GAP_PX,
          left: rect.left + window.scrollX,
        }

  // The listbox stays out of the focus order — the editor's
  // contenteditable keeps focus and forwards arrow / Enter keys via the
  // suggestion plugin -> our imperative `onKeyDown` handle. Mouse
  // interaction works through the items themselves.
  const widthClass = composerRect ? '' : 'w-56'
  // When docked above the composer OR flipped above due to viewport
  // overflow, lift the popover by its own height via translate so we
  // don't need to measure it. Without either marker, this collapses to
  // no transform (rendered below the caret as before).
  const transformClass = composerRect || flipAbove ? '-translate-y-full' : ''
  return createPortal(
    <ul
      ref={listRef}
      aria-label={t('mentions.popoverAriaLabel')}
      className={`fixed z-50 max-h-64 overflow-y-auto rounded-md border border-border bg-bg-elevated py-1 shadow-lg ${widthClass} ${transformClass}`}
      style={{
        ...positionStyle,
        // `position: fixed` ignores scroll, but the rect is in viewport
        // coords. Use `absolute` semantics by mixing window scroll into
        // top/left and switching to absolute positioning.
        position: 'absolute',
      }}
    >
      {items.length === 0 ? (
        <li className="px-3 py-2 text-sm text-fg-subtle">{t('mentions.noMatches')}</li>
      ) : (
        items.map((item, index) => {
          const active = index === selectedIndex
          return (
            <li key={item.id}>
              <button
                type="button"
                aria-selected={active}
                onMouseDown={(event) => {
                  // `mousedown` fires before the editor's blur, so we
                  // can commit without losing the selection.
                  event.preventDefault()
                  commit(index)
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm ${
                  active ? 'bg-bg-muted text-fg' : 'text-fg hover:bg-bg-muted'
                }`}
              >
                {/*
                 * Use the shared `<Avatar>` so the popover row matches
                 * sidebar / message-row / settings — same image rendering,
                 * deterministic color seeded by `item.id` (the user's
                 * UUID), and the same up-to-2-letter initial fallback.
                 */}
                <Avatar src={item.avatarUrl} name={item.name} colorSeed={item.id} size="h-7 w-7" />
                {/*
                 * Two-line row when `subtitle` is present (real users
                 * get their email; the @all synthetic entry gets
                 * "Everyone in this channel" in italic). Subtitle text
                 * is `text-fg-muted` for a lighter weight than the
                 * name. `min-w-0` on the wrapper lets the children
                 * `truncate` on long emails.
                 */}
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate">{item.name}</span>
                    <AiTag kind={item.kind} userId={item.id} />
                  </span>
                  {item.subtitle ? (
                    <span
                      className={`truncate text-xs text-fg-muted ${
                        item.subtitleStyle === 'italic' ? 'italic' : ''
                      }`}
                    >
                      {item.subtitle}
                    </span>
                  ) : item.kind === 'agent' ? (
                    <AgentModelLine userId={item.id} className="truncate text-xs text-fg-muted" />
                  ) : null}
                </span>
              </button>
            </li>
          )
        })
      )}
    </ul>,
    document.body,
  )
})
